import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { ErrorResponse } from '@core/error/error-response.model';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { CustomRxState } from '@core/state/rx.state';
import { ChargebackService } from '@api/services/chargeback.service';
import { ListCardTypeResponse } from '@api/models/ClientSupportApi/Contract/Chargeback/list-card-type-response';
import { SupportService } from '@api/services/support.service';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { GetPtTicketDataResponse } from '@api/models/ClientSupportApi/Contract/get-pt-ticket-data-response';
import { isNil, omitBy } from 'lodash-es';
import { AddPushMsgRequest } from '@api/models/ClientSupportApi/Contract/add-push-msg-request';
import { CouponGet } from '@api/models/ClientSupportApi/Contract/Coupon/coupon-get';
import {
    BlockService,
    ChangePhoneEmailService,
    CouponService,
    CourtesyService,
    EmailService,
    PhotoService,
    PrivService,
    RefundService,
    SmsService,
} from '@api/services';
import { GetClientDataIdResponse } from '@api/models/ClientSupportApi/Contract/get-client-data-id-response';
import { filter, tap } from 'rxjs/operators';
import { SetClientPhotoRequest } from '@api/models/ClientSupportApi/Contract/set-client-photo-request';
import { RefundRequest } from '@api/models/ClientSupportApi/Contract/Refunds/refund-request';
import { Refund } from '@api/models/ClientSupportApi/Contract/refund';
import {
    CheckMobilePhoneRequest,
} from '@api/models/ClientSupportApi/Contract/ChangePhoneEmail/check-mobile-phone-request';
import { SmsCode } from '@api/models/ClientSupportApi/Contract/ChangePhoneEmail/sms-code';
import { SetMobilePhoneRequest } from '@api/models/ClientSupportApi/Contract/ChangePhoneEmail/set-mobile-phone-request';
import { CheckEmailRequest } from '@api/models/ClientSupportApi/Contract/ChangePhoneEmail/check-email-request';
import { SetEmailRequest } from '@api/models/ClientSupportApi/Contract/ChangePhoneEmail/set-email-request';
import { GetRefundWarningResponse } from '@api/models/ClientSupportApi/Contract/Refunds/get-refund-warning-response';
import { TicketTotal } from '@api/models/ClientSupportApi/Contract/ticket-total';
import { CancelTicketRequest } from '@api/models/ClientSupportApi/Contract/Courtesy/cancel-ticket-request';
import { AddTicketRefundRequest } from '@api/models/ClientSupportApi/Contract/Refunds/add-ticket-refund-request';
import { ClientMedium } from '@api/models/ClientSupportApi/Contract/client-medium';
import { ClientSupportMediumStatus } from '../model/client-support-medium-status';
import { Zone } from '@api/models/ClientSupportApi/Contract/zone';
import { ClientTicket } from '@api/models/ClientSupportApi/Contract/client-ticket';
import { MediumType } from '@api/models/ClientSupportApi/Contract/medium-type';
import { ClientPaymentMethod } from '@api/models/ClientSupportApi/Contract/client-payment-method';
import { HistoryItem } from '@api/models/ClientSupportApi/Contract/history-item';
import { MoveUid } from '@api/models/ClientSupportApi/Contract/move-uid';
import { Priv } from '@api/models/ClientSupportApi/Contract/priv';
import { PrivDoctypes } from '@api/models/ClientSupportApi/Contract/priv-doctypes';
import { SetClientDataRequest } from '@api/models/ClientSupportApi/Contract/set-client-data-request';
import { ClientSearchBody, ClientSearchByPaymentsBody } from '../model/client-search';
import { ListPaymentResponse } from '@api/models/ClientSupportApi/Contract/Chargeback/list-payment-response';
import { ListClientResponse } from '@api/models/ClientSupportApi/Contract/list-client-response';
import { EmailCode } from '@api/models/BoService/Controllers/email-code';
import { HistoryState } from './history.state';
import { SearchTicketBody } from '../model/search-ticket-body';
import { PrivilegeAccountBody } from '../model/privilege-account';
import { SetBalanceUpdateRequest } from '@api/models/ClientSupportApi/Contract/Chargeback/set-balance-update-request';


export interface ISupportState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    client: GetClientDataIdResponse;
    cardTypes: ListCardTypeResponse[];
    mediumTypes: MediumType[];
    paymentMethods: ClientPaymentMethod[];
    ticketSearchResult: GetPtTicketDataResponse;
    couponSearchResult: CouponGet;
    refundWarning: GetRefundWarningResponse;
    clientTickets: TicketTotal[];
    clientMediums: ClientMedium[];
    clientMediumsCurrentStatus: ClientSupportMediumStatus;
    clientId: number;
    clientHistory: HistoryItem[];
    privileges: Priv[];
}

export interface ISupportAction {
    requestClient: GetClientDataIdResponse;
    searchTicket: GetPtTicketDataResponse;
    searchCoupon: CouponGet;
    requestClientTickets: TicketTotal[];
    requestClientMediums: ClientMedium[];
    setClientMediumsCurrentStatus: ClientSupportMediumStatus;
    setClientId: number;
    requestClientPrivilege: Priv[];
}

@Injectable({
    providedIn: 'root',
})
export class SupportState extends CustomRxState<ISupportState> {
    readonly #actions: RxActions<ISupportAction> = new RxActionFactory<ISupportAction>().create();
    readonly #chargebackService: ChargebackService = inject(ChargebackService);
    readonly #supportService: SupportService = inject(SupportService);
    readonly #couponService: CouponService = inject(CouponService);
    readonly #photoService: PhotoService = inject(PhotoService);
    readonly #blockService: BlockService = inject(BlockService);
    readonly #refundService: RefundService = inject(RefundService);
    readonly #changePhoneEmailService: ChangePhoneEmailService = inject(ChangePhoneEmailService);
    readonly #courtesyService: CourtesyService = inject(CourtesyService);
    readonly #privService: PrivService = inject(PrivService);
    readonly #smsService: SmsService = inject(SmsService);
    readonly #emailService: EmailService = inject(EmailService);
    readonly #historyState: HistoryState = inject(HistoryState);

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly cardTypes$: Observable<ListCardTypeResponse[]> = this.selectLazy('cardTypes', this.requestCardTypes);
    public readonly refundWarning$: Observable<GetRefundWarningResponse> = this.selectLazy('refundWarning', this.requestRefundWarning);
    public readonly mediumTypes$: Observable<MediumType[]> = this.selectLazy('mediumTypes', this.requestMediumTypes);
    public readonly paymentMethods$: Observable<ClientPaymentMethod[]> = this.selectLazy('paymentMethods', this.requestPaymentMethods);
    public readonly privileges$: Observable<Priv[]> = this.selectLazy('privileges', this.requestPrivileges);
    public readonly ticketSearchResult$: Observable<GetPtTicketDataResponse> = this.select('ticketSearchResult');
    public readonly couponSearchResult$: Observable<CouponGet> = this.select('couponSearchResult');
    public readonly client$: Observable<GetClientDataIdResponse> = this.select('client');
    public readonly clientTickets$: Observable<TicketTotal[]> = this.select('clientTickets');
    public readonly clientMediums$: Observable<ClientMedium[]> = this.select('clientMediums');
    public readonly clientMediumsCurrentStatus$: Observable<ClientSupportMediumStatus> = this.select('clientMediumsCurrentStatus');


    constructor() {
        super();

        this.connectSelectors();
        this.setDefaultState();
    }

    private requestCardTypes(): Observable<ListCardTypeResponse[]> {
        return this.#chargebackService.apiSupportListCardTypeGet();
    }

    private requestRefundWarning(): Observable<GetRefundWarningResponse> {
        return this.#refundService.apiSupportRefundWarningGet();
    }

    private requestMediumTypes(): Observable<MediumType[]> {
        return this.#supportService.apiSupportUidsTypesGet();
    }

    private requestPaymentMethods(): Observable<ClientPaymentMethod[]> {
        return this.#supportService.apiSupportPaymethodsGet();
    }

    private requestPrivileges(): Observable<Priv[]> {
        return this.#privService.apiSupportListPrivCategGet();
    }

    public sendClientMessage(body: AddPushMsgRequest): Observable<void> {
        return this.#supportService.apiSupportAddPushMsgPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.SendClientMessage),
        );
    }

    public searchTicket(body: SearchTicketBody): void {
        this.set({ ticketSearchResult: null });

        this.hold(
            this.#supportService.apiSupportGetPtTicketDataGet(omitBy(body, isNil)).pipe(
                startWithLoading(this, StartWithUnit.SearchTicket),
            ),
            this.#actions.searchTicket,
        );
    }

    public searchCoupon(cpnNo: string): void {
        this.set({ couponSearchResult: null });

        this.hold(
            this.#couponService.apiSupportDiscCouponGetGet({ cpnNo }).pipe(
                startWithLoading(this, StartWithUnit.SearchCoupon),
            ),
            this.#actions.searchCoupon,
        );
    }

    public requestClient(clientId: number): Observable<GetClientDataIdResponse> {
        return this.#supportService.apiSupportGetClientDataIdGet({ clientId }).pipe(
            startWithLoading(this, StartWithUnit.SearchClient),
            tap((client: GetClientDataIdResponse) => this.#actions.requestClient(client)),
        );
    }

    public refreshClient(clientId: number): void {
        this.hold(
            this.#supportService.apiSupportGetClientDataIdGet({ clientId }).pipe(
                startWithLoading(this, StartWithUnit.SearchClient),
            ),
            this.#actions.requestClient,
        );
    }

    public setPhoto(id: number, body: SetClientPhotoRequest): Observable<void> {
        return this.#photoService.apiSupportClientsIdPhotoPost({ body, id }).pipe(
            tap(() => this.refreshClient(id)),
        );
    }

    public deletePhoto(id: number): Observable<void> {
        return this.#photoService.apiSupportClientsIdPhotoDelete({ id }).pipe(
            tap(() => this.refreshClient(id)),
        );
    }

    public blockClient(id: number, note: string): Observable<void> {
        return this.#blockService.apiSupportClientsIdBlockGet({ id, note }).pipe(
            startWithLoading(this, StartWithUnit.BlockUnblockClient),
            tap(() => this.refreshClient(id)),
        );
    }

    public unBlockClient(id: number, note: string): Observable<void> {
        return this.#blockService.apiSupportClientsIdUnblockGet({ id, note }).pipe(
            startWithLoading(this, StartWithUnit.BlockUnblockClient),
            tap(() => this.refreshClient(id)),
        );
    }

    public createRefund(id: number, body: RefundRequest): Observable<Refund> {
        return this.#refundService.apiSupportClientsIdRefundPost({ id, body }).pipe(
            startWithLoading(this, StartWithUnit.CreateRefund),
            tap(() => this.refreshClient(id)),
        );
    }

    public changeBalance(body: SetBalanceUpdateRequest): Observable<void> {
        return this.#chargebackService.apiSupportSetBalanceUpdatePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.ReduceBalance),
            tap(() => this.refreshClient(body.client)),
        );
    }

    public checkMobilePhoneClientBeforeChange(body: CheckMobilePhoneRequest): Observable<SmsCode> {
        return this.#changePhoneEmailService.apiSupportCheckMobilePhonePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.CheckOrSetMobilePhone),
        );
    }

    public changeMobilePhone(body: SetMobilePhoneRequest): Observable<void> {
        return this.#changePhoneEmailService.apiSupportSetMobilePhonePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.CheckOrSetMobilePhone),
            tap(() => this.refreshClient(body.client)),
        );
    }

    public checkEmailClientBeforeChange(body: CheckEmailRequest): Observable<SmsCode> {
        return this.#changePhoneEmailService.apiSupportCheckEmailPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.CheckOrSetEmail),
        );
    }

    public changeEmail(body: SetEmailRequest): Observable<void> {
        return this.#changePhoneEmailService.apiSupportSetEmailPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.CheckOrSetMobilePhone),
            tap(() => this.refreshClient(body.client)),
        );
    }

    public requestClientTickets(id: number): void {
        this.hold(
            this.#supportService.apiSupportClientsIdTicketsGet({ id }),
            this.#actions.requestClientTickets,
        );
    }

    public requestClientMediums(id: number, status: ClientSupportMediumStatus): void {
        this.patch('clientMediums', null);

        this.hold(
            this.#supportService.apiSupportClientsIdUidsGet({ id, status }).pipe(
                startWithLoading(this, StartWithUnit.LoadClientMediums),
            ),
            this.#actions.requestClientMediums,
        );
    }

    public setClientId(clientId: number): void {
        this.#actions.setClientId(clientId);
    }

    public setClientMediumsCurrentStatus(status: ClientSupportMediumStatus): void {
        this.#actions.setClientMediumsCurrentStatus(status);
    }

    public cancelClientTicket(body: CancelTicketRequest, note: string): Observable<void> {
        body.note = note;

        return this.#courtesyService.apiSupportCancelTicketPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.CancelClientTicket),
            tap(() => this.requestClientTickets(body.client)),
        );
    }

    public refundClientTicket(client: number, tktId: number, body: AddTicketRefundRequest): Observable<void> {
        return this.#refundService.apiSupportRefundClientTktIdPost({ client, tktId, body }).pipe(
            startWithLoading(this, StartWithUnit.RefundClientTicket),
            tap(() => {
                this.requestClientTickets(client);

                setTimeout(() => {
                    this.requestClientMediums(client, this.get('clientMediumsCurrentStatus'));
                });
            }),
        );
    }

    public mediumZones(id: number): Observable<Zone[]> {
        return this.#supportService.apiSupportUidsIdZonesGet({ id });
    }

    public mediumTickets(id: number, uid: number): Observable<ClientTicket[]> {
        return this.#supportService.apiSupportClientsIdUidsUidTicketsGet({ id, uid });
    }

    public removeClientMedium(client: number, mediumId: number, note?: string): Observable<void> {
        return this.#supportService.apiSupportRemoveIdDelete({ client, mediumId, note }).pipe(
            startWithLoading(this, StartWithUnit.DeleteClientMedium),
            tap(() =>
                setTimeout(() => this.requestClientMediums(client, this.get('clientMediumsCurrentStatus')))),
        );
    }

    public transferTicket(id: number, body: MoveUid): Observable<void> {
        return this.#supportService.apiSupportClientsIdCardMovePost({ id, body }).pipe(
            startWithLoading(this, StartWithUnit.TransferClientTicket),
            tap(() => {
                this.requestClientTickets(id);

                setTimeout(() => {
                    this.requestClientMediums(id, this.get('clientMediumsCurrentStatus'));
                });
            }),
        );
    }

    public loadPrivilegeDocTypes(privId: number): Observable<PrivDoctypes[]> {
        return this.#privService.apiSupportListPrivDoctypesPrivIdGet({ privId }).pipe(
            startWithLoading(this, StartWithUnit.LoadDocTypes),
        );
    }

    /**
     * TODO Implement overload function
     */
    public setPrivilegeForAccountOrMedium(body: PrivilegeAccountBody, clientId: number): Observable<void> {
        return body.mediumId ? this.#privService.apiSupportSetIdPrivPost({ body: omitBy(body, isNil) }).pipe(
            startWithLoading(this, StartWithUnit.SetClientOrMediumPrivilege),
            tap(() => this.requestClientMediums(clientId, this.get('clientMediumsCurrentStatus'))),
        ) : this.#supportService.apiSupportSetClientPrivPost({ body: omitBy(body, isNil) }).pipe(
            startWithLoading(this, StartWithUnit.SetClientOrMediumPrivilege),
            tap(() => this.refreshClient(clientId)),
        );
    }

    public deletePrivilegeForAccountOrMedium(privId: number, clientId: number, mediumId?: number, note?: string): Observable<unknown> {
        return mediumId ? this.#privService.apiSupportRemoveIdPrivDelete({ privId, mediumId, note }).pipe(
            startWithLoading(this, StartWithUnit.DeleteClientOrMediumPrivilege),
            tap(() => this.requestClientMediums(clientId, this.get('clientMediumsCurrentStatus'))),
        ) : this.#privService.apiSupportRemoveClientPrivDelete({ privId, clientId, note }).pipe(
            startWithLoading(this, StartWithUnit.DeleteClientOrMediumPrivilege),
            tap(() => this.refreshClient(clientId)),
        );
    }

    public setClientPersonalData(body: SetClientDataRequest): Observable<void> {
        return this.#supportService.apiSupportSetClientDataPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.SetClientPersonalData),
            tap(() => this.refreshClient(body.clientId)),
        );
    }

    public searchClientsByPayments(body: ClientSearchByPaymentsBody): Observable<ListPaymentResponse[]> {
        return this.#chargebackService.apiSupportListPaymentGet(body).pipe(
            startWithLoading(this, StartWithUnit.SearchClients),
        );
    }

    public searchClient(body: ClientSearchBody): Observable<ListClientResponse[]> {
        return this.#supportService.apiSupportListClientGet({
            ...(body.phone ? { mobilePhone: body.phone } : null),
            ...(body.email ? { email: body.email } : null),
            ...(body.clientId ? { clientId: body.clientId } : null),
            ...(body.mediumN ? { mediumN: body.mediumN } : null),
        }).pipe(
            startWithLoading(this, StartWithUnit.SearchClients),
        );
    }

    public sendCodeForCheckPhoneOrEmail(phone: string, email?: string): Observable<SmsCode | EmailCode> {
        return phone ? this.#smsService.apiSmsSendSmsCodeGet({ phone }) : this.#emailService.apiEmailSendEmailCodeGet({ email });
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            couponSearchResult: null,
            ticketSearchResult: null,
            cardTypes: null,
            mediumTypes: null,
            client: null,
            clientTickets: null,
            clientMediums: null,
            clientMediumsCurrentStatus: ClientSupportMediumStatus.Active,
            clientHistory: null,
            privileges: null,
        });
    }

    private connectSelectors(): void {
        this.connect('ticketSearchResult', this.#actions.searchTicket$);
        this.connect('couponSearchResult', this.#actions.searchCoupon$);
        this.connect('clientTickets', this.#actions.requestClientTickets$);
        this.connect('clientMediums', this.#actions.requestClientMediums$);
        this.connect('client', this.#actions.requestClient$);
        this.connect('privileges', this.#actions.requestClientPrivilege$);
        this.connect('clientMediumsCurrentStatus', this.#actions.setClientMediumsCurrentStatus$.pipe(
            tap((status: ClientSupportMediumStatus) => {
                this.requestClientMediums(this.get('clientId'), status);
            }),
        ));

        this.connect('clientId', this.#actions.setClientId$.pipe(
            filter(Boolean),
            tap((clientId: number) => {
                this.#historyState.set({ clientHistory: null });

                setTimeout(() => {
                    this.requestClientTickets(clientId);
                    this.setClientMediumsCurrentStatus(ClientSupportMediumStatus.Active);
                });
            }),
        ));
    }
}
